import React, {
  FunctionComponent,
  useState,
  SyntheticEvent,
  useRef,
} from 'react';
import withStyles from 'react-jss';
import { isEmpty } from 'ramda';
import { Checkbox, Grid, Header, Icon } from 'semantic-ui-react';
import { format } from 'd3-format';
import {
  ComposedChart,
  Line,
  Area,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  XAxis,
} from 'recharts';

import Calendar from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/components/Calendar';
import {
  LiftResults,
  Data,
  tickFormatter,
  chartMargin,
  DataType,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.helpers';
import DownloadWidget from 'components/DownloadWidget';
import { ClassProp } from 'helpers/types';
import { theme } from 'config';
import { Timeslice } from 'scenes/Report/Report.helper';
import { getBaseStyles } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.styles';
import CustomTooltip from 'components/CustomTooltip';
import FormattedCurrency from 'scenes/Report/components/FormattedCurrency';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import getQuery from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.queries';
import { Params } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  data: Data;
  params: Params;
};

const styles = getBaseStyles(
  {
    chart: { height: 400 },
    chartContent: {
      padding: '1rem !important',
    },
    header: {
      display: 'flex !important',
      alignItems: 'center !important',
      fontWeight: '500 !important',
    },
    headerIcon: {
      color: '#8fe2b4 !important',
    },
  },
  { checkboxColor: theme.seafoamGreen },
);

const controlGroupTooltipFields = (
  showControlGroup: boolean,
  type: DataType,
) => {
  const totalSales = {
    label: 'Total Sales',
    value: 'exposedTotalSales',
    formatter: (value: number) => <FormattedCurrency value={value} />,
  };

  const controlGroup = {
    label: 'Control Group',
    value: 'scaledControlSales',
    formatter: (value: number) => <FormattedCurrency value={value} />,
  };

  const liftPercent = {
    formatter: format('.2%'),
    label: 'Lift',
    value: ({ exposedTotalSales, scaledControlSales }: LiftResults) =>
      (exposedTotalSales - scaledControlSales) / scaledControlSales,
  };

  if (showControlGroup && type === 'control') {
    return [totalSales, controlGroup, liftPercent];
  }

  return [totalSales];
};

const bulkResponseMapping = ({
  totalSales,
  scaledControlSales,
  exposedTotalSales,
  periodStart,
}: any) => ({
  Day: periodStart,
  'Total Sales': format('$,')(totalSales),
  'Control Group': format('$,')(exposedTotalSales),
  Lift: format('.2%')(
    (exposedTotalSales - scaledControlSales) / (scaledControlSales || 1),
  ),
});

const Sales: FunctionComponent<Props> = ({
  classes,
  data: { type, results, timeslice = Timeslice.DAILY },
  params,
}) => {
  const contentRef = useRef(null);
  const [showControlGroup, setShowControlGroup] = useState<boolean>(true);
  const isControlType = type === 'control';
  const { isExternalUser } = useUserMgmt();

  const toggleControlGroup = (
    _: SyntheticEvent<HTMLElement, Event>,
    { checked }: any,
  ) => {
    setShowControlGroup(checked);
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const renderHeader = (title: string, classes: Styles) => (
    <Header className={classes.header}>
      <Icon
        className={classes.headerIcon}
        name={isControlType ? 'chart line' : 'area chart'}
      />
      {title}

      {isControlType && (
        <Checkbox
          label="Control Group"
          className={classes.headerCheckBox}
          checked={showControlGroup}
          onChange={toggleControlGroup}
        />
      )}
    </Header>
  );

  const Chart = (isControlType ? Line : Area) as React.ElementType;

  return (
    <>
      <div ref={contentRef} className={classes.ref}>
        <Grid.Row centered className={classes.chartContent}>
          <Grid.Column
            textAlign="center"
            verticalAlign="middle"
            width={16}
            columns={1}
            className={classes.noPadding}
          >
            <Calendar
              timeslice={timeslice}
              liftResults={results as LiftResults[]}
              chartType={'sales'}
            />

            <Grid.Column>
              {isEmpty(results) ? (
                <Header as="h3">
                  There is no data to display at this time.
                </Header>
              ) : (
                <div className={classes.chart}>
                  <ResponsiveContainer>
                    <ComposedChart data={results} margin={chartMargin}>
                      <YAxis tickFormatter={tickFormatter()} />
                      <XAxis tick={false} scale="point" />

                      <Tooltip
                        content={
                          <CustomTooltip
                            fields={controlGroupTooltipFields(
                              showControlGroup,
                              type,
                            )}
                            xAxis={{
                              label: 'Day',
                              key: 'periodStart',
                              type: 'date',
                            }}
                          />
                        }
                      />

                      {showControlGroup && (
                        <Area
                          type="linear"
                          dataKey="scaledControlSales"
                          fill={theme.seafoamGreen}
                          stroke={theme.seafoamGreen}
                        />
                      )}

                      <Chart
                        type="linear"
                        dataKey="exposedTotalSales"
                        stroke={theme.seafoamGreen}
                        fill={theme.seafoamGreen}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Grid.Column>
          </Grid.Column>

          <Grid.Column width={16} className={classes.calendarLegend}>
            {renderHeader('Total Sales', classes)}
          </Grid.Column>
        </Grid.Row>
      </div>

      <DownloadWidget
        nodeRef={contentRef}
        className={classes.download}
        data={timeslice === 'DAILY' && results.map(bulkResponseMapping)}
        options={['PNG', 'PDF', 'CSV']}
        imageName="Sales"
        csvName="sales_lift_by_day"
        bulkConfig={{
          query: getQuery(isExternalUser),
          responseMapping: bulkResponseMapping,
          variables: {
            params: { ...params, timeslice: 'DAILY' },
          },
          dataPath: isControlType
            ? ['controlGroup', 'liftResults']
            : ['transactionSummary', 'result'],
        }}
      />
    </>
  );
};

export default withStyles(styles)(Sales);
